import { fillPath, route } from "./routes.js";
import { z } from "zod";
import { ORG_MEMBERS_MAX_PAGE_SIZE, ORG_MEMBERS_MAX_SEARCH_LENGTH, ORG_MEMBERS_SEARCH_PATTERN } from "@zion/sdk-contracts";
//#region src/users.ts
/**
* `/v1/admin/users/*` — the ten routes the admin app's users pages call.
*
* Producer: `AdminUsersController` and `AdminUserAppVisibilityController` in
* `cloud/dp/core-api/src/admin/`. Consumer: the admin app's `AdminClient`. Every response schema
* below is the controller's body, not the shell's read type, and they differ in four places:
*
* 1. `listUsers` returns `PaginatedResponseDto` — five keys including `totalPages`. The shell's
*    local `PaginatedResponse` and `@zion/sdk-contracts`' `PaginatedResponse` both declare four
*    and omit `totalPages`, so neither is this route's envelope and neither is imported here.
* 2. Every user row is `UserPublicRow` (13 columns) plus the three fields `toAdminUserView` adds.
*    The shell's `UserListItem` declares 9 of those 16 and its `UserDetail` omits
*    `organizationId`, `displayName`, `role`, `status`, `updatedAt`, `forcePasswordChange` and
*    `googleSub`.
* 3. `groups`, `effectiveRoles` and `channels` are optional in the shell's `UserDetail` for a
*    rolling-deploy reason. `UserService.getUserDetail` always returns all three, so they are
*    required here.
* 4. `updateUserRole`, `deactivateUser` and `reactivateUser` are typed `Promise<void>` in the
*    shell client. All three controllers return a one-key `{ message }` body with status 200.
*
* Permission keys: `users:read` on the two reads; `users:manage` on the profile update,
* deactivate, reactivate, membership removal and session revocation; `org:manage` on the role
* change, the visibility preview and the ownership transfer.
*
* Direction rule (R-28): every response schema here is `z.object`, so a member core-api adds is
* stripped rather than refused and one new column cannot red every page that reads the route.
* Every request schema is `z.strictObject`, so a key the server would reject is refused before
* the request leaves the client. No shape in this file travels in both directions.
*/
/**
* One `auth.sessions` row inside {@link userDetailSchema}, projected by
* `sessionPublicColumns`. Token-hash-free by construction at the repository boundary.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/sessions.ts`. `ipAddress` is Postgres `inet`, which
* reaches the wire as the address string.
*/
const adminUserSessionSchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	organizationId: z.uuid(),
	isRevoked: z.boolean(),
	ipAddress: z.string().nullable(),
	userAgent: z.string().nullable(),
	expiresAt: z.iso.datetime(),
	createdAt: z.iso.datetime(),
	/** `'user'` or `'mcp'` — the table's CHECK constraint admits nothing else. */
	sessionType: z.enum(["user", "mcp"]),
	label: z.string().nullable(),
	lastUsedAt: z.iso.datetime().nullable(),
	rotatedAt: z.iso.datetime().nullable(),
	familyId: z.uuid().nullable()
});
/**
* One `auth.passkeys` row inside {@link userDetailSchema}.
*
* `UserService.getUserDetail` returns `ctx.passkeys.listByUserId(userId)` verbatim, so the whole
* row ships — including `publicKey`, a `bytea` column whose JSON encoding is the pg driver's, not
* this contract's. It is `z.unknown()` because no shape here can be honest about it. The shell's
* `PasskeyInfo` has no importer and does not describe this body.
*
* Owner: `cloud/dp/core-api/src/db/schema/auth/passkeys.ts`.
*/
const adminUserPasskeySchema = z.object({
	id: z.uuid(),
	userId: z.uuid(),
	organizationId: z.uuid(),
	credentialId: z.string(),
	/** The raw `bytea` column as the pg driver encodes it. Not a shape this contract fixes. */
	publicKey: z.unknown(),
	signCount: z.number().int(),
	transports: z.array(z.string()),
	deviceName: z.string().nullable(),
	createdAt: z.iso.datetime(),
	lastUsedAt: z.iso.datetime().nullable(),
	/**
	* Which relying party the passkey belongs to (kernel apps R-131, R-133): `kernel` for a passkey of
	* the kernel origin, `legacy` for a passkey of the data plane's own relying party (the shell
	* origin), `null` for a row that no ceremony accepts. `PasskeyController.listPasskeys` writes it on
	* each entry of `GET /v1/auth/passkeys`. The admin detail row of {@link userDetailSchema} is the
	* raw `auth.passkeys` row, which holds `rpId` and no `relyingParty`, so the member is optional
	* here. A reader of the passkey list makes it required (`passkeyListSchema` of
	* `@zion/kernel-shell`). Any other value fails the parse.
	*/
	relyingParty: z.enum(["kernel", "legacy"]).nullable().optional()
});
/**
* One direct group membership inside {@link userDetailSchema}. Nested groups are not expanded.
*
* Owner: `UserGroupRef` in `cloud/dp/core-api/src/admin/user.service.ts`.
*/
const userGroupRefSchema = z.object({
	id: z.uuid(),
	name: z.string()
});
/**
* One role the user holds, tagged with where it came from. Not de-duplicated by slug: one slug can
* arrive from two groups and each is a separate thing an admin might revoke.
*
* `defined` is `false` when the assignment names a slug with no `org_role_definitions` row, which
* confers nothing at runtime. `groupId` and `groupName` are present only when `source` is
* `'group'`.
*
* Owner: `EffectiveRole` in `cloud/dp/core-api/src/admin/user.service.ts`.
*/
const effectiveRoleSchema = z.object({
	slug: z.string(),
	source: z.enum([
		"built-in",
		"direct",
		"group"
	]),
	groupId: z.uuid().optional(),
	groupName: z.string().optional(),
	defined: z.boolean()
});
/**
* One channel the user is entitled to, and the group that grants it. The entitlement pool, never
* the effective channel: the ring floor and the user's own selection are resolved elsewhere.
*
* Owner: `UserChannelGrant` in `cloud/dp/core-api/src/admin/user.service.ts`.
*/
const userChannelGrantSchema = z.object({
	channelId: z.uuid(),
	channelName: z.string(),
	groupId: z.uuid(),
	groupName: z.string()
});
/**
* The 13 `userPublicColumns` plus the three fields `toAdminUserView` derives. Shared by the list
* row and the detail body, which is why it is declared once.
*
* `roles` is the built-in role wrapped in an array, never the effective set; read
* {@link userDetailSchema}'s `effectiveRoles` for that. `isActive` is `status === 'active'`.
* `fullName` is `displayName` or the empty string.
*
* Owner: `userPublicColumns` in `cloud/dp/core-api/src/db/schema/auth/users.ts` and
* `toAdminUserView` in `admin-users.controller.ts`.
*/
const adminUserViewSchema = z.object({
	id: z.uuid(),
	organizationId: z.uuid(),
	email: z.string(),
	displayName: z.string(),
	avatarUrl: z.string().nullable(),
	/** The built-in role slug. A custom slug is legal here; the column is a bare `varchar`. */
	role: z.string(),
	emailVerified: z.boolean(),
	/**
	* The canonical value set is `active`, `deactivated`, `removed`, declared once as
	* `USER_STATUSES` in `cloud/dp/core-api/src/db/schema/auth/users.ts` (#1892).
	* `drizzle/auth/0042_users_status_backfill.sql` moves every existing row onto it; a
	* later release adds the `users_status_check` constraint that enforces it.
	*
	* ⛔ This field stays `z.string()` ON PURPOSE (ruling R6), and narrowing it to that enum is the
	* one change to avoid. THIS SCHEMA IS THE WIRE CONTRACT THE ADMIN APP PARSES, not something
	* core-api validates its own responses against — core-api references none of these schemas
	* at runtime. So a narrowed schema does not fail on the server; it makes a PRE-MIGRATION ROW
	* UNREADABLE BY THE CLIENT, which turns a cosmetic status string into a broken page for
	* every data plane whose migration has not run yet. The column is the authority; this
	* schema describes the wire.
	*
	* ⚠️ `removed` IS IN THE SET BUT NOT WRITABLE THROUGH {@link UPDATE_USER_ROUTE}
	* (ruling R11). `UserService.removeMember`, behind {@link REMOVE_MEMBER_ROUTE}
	* (`DELETE /v1/admin/users/{id}/membership`), is its only writer, because the value also
	* means the principal's group memberships and role grants were deleted in the same
	* transaction. {@link updateUserRequestSchema} accepts `active` and `deactivated` only.
	*/
	status: z.string(),
	lastLoginAt: z.iso.datetime().nullable(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	forcePasswordChange: z.boolean(),
	googleSub: z.string().nullable(),
	roles: z.array(z.string()),
	isActive: z.boolean(),
	fullName: z.string()
});
/**
* One row of `GET /v1/admin/users`. {@link adminUserViewSchema} plus `extraRoleCount`, the count of
* distinct role slugs held beyond the built-in one. `0` means the built-in role is the whole story.
*
* `extraRoleCount` is on the list route only: `UserService.withExtraRoleCounts` computes it and
* `getUserDetail` does not.
*
* Owner: `UserService.listUsers`. The shell's `UserListItem` is a 9-key subset of this.
*/
const userListItemSchema = adminUserViewSchema.extend({ extraRoleCount: z.number().int().min(0) });
/**
* The body of `GET /v1/admin/users/{id}`: the user view plus its sessions, passkeys, direct
* groups, effective roles and channel entitlements.
*
* No `extraRoleCount` — the detail page renders the roles themselves.
*
* Owner: `UserService.getUserDetail`. The shell's `UserDetail` omits seven of the user columns and
* marks the last three arrays optional; this controller always sends them.
*/
const userDetailSchema = adminUserViewSchema.extend({
	sessions: z.array(adminUserSessionSchema),
	passkeys: z.array(adminUserPasskeySchema),
	groups: z.array(userGroupRefSchema),
	effectiveRoles: z.array(effectiveRoleSchema),
	channels: z.array(userChannelGrantSchema)
});
/** `GET /v1/admin/users` — list the organization's users. Requires `users:read`. */
const LIST_USERS_ROUTE = route("GET", "/v1/admin/users");
/**
* `GET /v1/admin/users/{id}` — one user with sessions, passkeys, groups, roles and channels.
* Requires `users:read`.
*/
const GET_USER_ROUTE = route("GET", "/v1/admin/users/{id}");
/**
* `GET /v1/admin/users/{id}/role-app-visibility-preview` — how many apps a role change would
* cost this user. Requires `org:manage`, the same key as the write it precedes.
*/
const GET_USER_ROLE_APP_VISIBILITY_PREVIEW_ROUTE = route("GET", "/v1/admin/users/{id}/role-app-visibility-preview");
/**
* `PATCH /v1/admin/users/{id}/role` — change the built-in role. Requires `org:manage`.
*
* Errors: 400 when `role` names no role; 400 `New owner must be an active user` when `role` is
* `owner`, the target is not an owner yet, and its `status` is not `active` (#3333; re-applying
* `owner` to a row that already holds it is a 200 whatever its status); 403 when the caller may
* not grant the role; 403 `Cannot change the role of the sole owner. Transfer ownership first.` when the
* target is the last active owner; 404 `User not found`.
*/
const UPDATE_USER_ROLE_ROUTE = route("PATCH", "/v1/admin/users/{id}/role");
/**
* `DELETE /v1/admin/users/{id}` — deactivate the user and revoke every session. Requires
* `users:manage`.
*
* Errors: 403 `Cannot deactivate the organization owner` when the path user holds the `owner`
* role (#3333; transfer ownership first); 404 `User not found`; 400 for a path id that is not a
* uuid.
*/
const DEACTIVATE_USER_ROUTE = route("DELETE", "/v1/admin/users/{id}");
/**
* `POST /v1/admin/users/{id}/reactivate` — reactivate a deactivated user. Requires
* `users:manage`.
*/
const REACTIVATE_USER_ROUTE = route("POST", "/v1/admin/users/{id}/reactivate");
/**
* `PATCH /v1/admin/users/{id}` — update a user's display name, avatar URL or status. Status 200
* with the saved {@link adminUserViewSchema} row; 404 `User not found`; 400 for a body the
* producer's `updateUserSchema` refuses, or a path id that is not a uuid (`ParseUUIDPipe`); 403
* `Cannot deactivate the organization owner` when the body carries `status: 'deactivated'` and
* the path user holds the `owner` role (#3333). Requires `users:manage`.
*
* A body that carries `status` also revokes the user's sessions unless the committed row is
* `active` when the revoke runs (`revokeAllUserSessionsUnlessActive`), so this route is a second
* deactivation route beside {@link DEACTIVATE_USER_ROUTE}.
*/
const UPDATE_USER_ROUTE = route("PATCH", "/v1/admin/users/{id}");
/**
* `DELETE /v1/admin/users/{id}/membership` — remove the user from the organization. Status 200
* with `{ message: 'Member removed' }`. Requires `users:manage`.
*
* Errors: 403 `Cannot remove the organization owner`; 404 `User not found`; 400 for a path id
* that is not a uuid. `UserService.removeMember` sets the user's status to `removed` and deletes
* the user's group memberships and role grants in one transaction; the controller then revokes
* every session unconditionally, because live tokens still carry the deleted grants.
*/
const REMOVE_MEMBER_ROUTE = route("DELETE", "/v1/admin/users/{id}/membership");
/**
* `POST /v1/admin/users/{id}/transfer-ownership` — make the path user the organization owner.
* Status 201, Nest's default for a `@Post` with no `@HttpCode`; `openapi.json` documents 200, so
* a client accepts any 2xx. The body is `{ message: 'Ownership transferred' }`. Requires
* `org:manage`.
*
* Errors: 403 `Only the current owner can transfer ownership` when the caller is not the owner;
* 400 `New owner must be an admin` when the target's built-in role is not `admin`; 400
* `New owner must be an active admin` when the target's `status` is not `active` (#3333); 404
* `User not found` when the target is not in the organization; 400 for a path id that is not a
* uuid. On success the target becomes `owner` and the caller becomes `admin` in one transaction,
* so a client re-reads its own permissions.
*/
const TRANSFER_OWNERSHIP_ROUTE = route("POST", "/v1/admin/users/{id}/transfer-ownership");
/**
* `DELETE /v1/admin/users/{id}/sessions` — revoke every active session the user holds. Status 200
* with `{ message: 'Sessions revoked' }`, whatever the user's status. 404 `User not found` from
* the org-scoped lookup that runs first; 400 for a path id that is not a uuid. Requires
* `users:manage`.
*/
const REVOKE_USER_SESSIONS_ROUTE = route("DELETE", "/v1/admin/users/{id}/sessions");
/**
* Fill `/v1/admin/users/{id}`. The placeholder is `id`, the name `@Param('id')` puts in
* `openapi.json`. Refuses a value with a slash, a space or any other non-param character.
*/
function adminUserPath(userId) {
	return fillPath(GET_USER_ROUTE.path, { id: userId });
}
/** Fill `/v1/admin/users/{id}/role-app-visibility-preview`. */
function adminUserRoleAppVisibilityPreviewPath(userId) {
	return fillPath(GET_USER_ROLE_APP_VISIBILITY_PREVIEW_ROUTE.path, { id: userId });
}
/** Fill `/v1/admin/users/{id}/role`. */
function adminUserRolePath(userId) {
	return fillPath(UPDATE_USER_ROLE_ROUTE.path, { id: userId });
}
/** Fill `/v1/admin/users/{id}/reactivate`. */
function adminUserReactivatePath(userId) {
	return fillPath(REACTIVATE_USER_ROUTE.path, { id: userId });
}
/**
* The path id of the three user routes this contract added for zion-admin-app
* `uncovered-admin-routes`. The producer runs `ParseUUIDPipe` on each and answers 400 for any
* other value, so the builder refuses a non-uuid before a request is built. The four older user
* builders keep `pathParamSchema`, because narrowing them would change what a 0.4.x caller may pass.
*/
const userIdPathParamSchema = z.uuid();
/**
* Fill `/v1/admin/users/{id}/membership`, the path of {@link REMOVE_MEMBER_ROUTE}.
*
* @param userId the user's uuid. Throws a `ZodError` when it is not a uuid: the empty string, a
*   slug, a traversal such as `../login`, a value with a space, or anything over 36 characters.
*   No request is built for a refused id.
*/
function adminUserMembershipPath(userId) {
	return fillPath(REMOVE_MEMBER_ROUTE.path, { id: userId }, { id: userIdPathParamSchema });
}
/**
* Fill `/v1/admin/users/{id}/transfer-ownership`, the path of {@link TRANSFER_OWNERSHIP_ROUTE}.
* Same bound and failure as {@link adminUserMembershipPath}.
*/
function adminUserTransferOwnershipPath(userId) {
	return fillPath(TRANSFER_OWNERSHIP_ROUTE.path, { id: userId }, { id: userIdPathParamSchema });
}
/**
* Fill `/v1/admin/users/{id}/sessions`, the path of {@link REVOKE_USER_SESSIONS_ROUTE}. Same bound
* and failure as {@link adminUserMembershipPath}.
*/
function adminUserSessionsPath(userId) {
	return fillPath(REVOKE_USER_SESSIONS_ROUTE.path, { id: userId }, { id: userIdPathParamSchema });
}
/**
* The query of `GET /v1/admin/users`. Every key is optional; `UserService.listUsers` applies page 1
* and pageSize 20 when they are absent.
*
* `search` carries the org-members read's own bounds and pattern, imported rather than respelled:
* a control character here reaches Postgres as a bound `ILIKE` pattern and raises `22021`.
* `page` has a floor of 1 because an unfloored value reached Postgres as a negative `OFFSET`, and
* `pageSize` has the org-members ceiling because an uncapped value returned the whole user table
* in one body.
*
* The controller binds all three per key, so this object is the client's shape rather than a schema
* core-api parses whole.
*/
const listUsersRequestSchema = z.strictObject({
	page: z.coerce.number().int().min(1).optional(),
	pageSize: z.coerce.number().int().min(1).max(ORG_MEMBERS_MAX_PAGE_SIZE).optional(),
	search: z.string().min(1).max(ORG_MEMBERS_MAX_SEARCH_LENGTH).regex(ORG_MEMBERS_SEARCH_PATTERN).optional()
});
/**
* The body of `GET /v1/admin/users` — `PaginatedResponseDto<UserListItem>`, five keys.
*
* `totalPages` is `Math.ceil(total / pageSize)` and is the key that makes
* `@zion/sdk-contracts`' four-key `PaginatedResponse` the wrong shape for this route.
*
* Owner: `paginatedResponseSchema` in `cloud/dp/core-api/src/shared/dto/pagination.dto.ts`.
*/
const listUsersResponseSchema = z.object({
	data: z.array(userListItemSchema),
	total: z.number().int().min(0),
	page: z.number().int().min(1),
	pageSize: z.number().int().min(1),
	totalPages: z.number().int().min(0)
});
/** The body of `GET /v1/admin/users/{id}`. */
const getUserResponseSchema = userDetailSchema;
/**
* The query of `GET /v1/admin/users/{id}/role-app-visibility-preview`.
*
* A bounded plain string, deliberately not an enum: the preview must be able to answer for a slug
* the write would refuse, and a closed set here would make it unavailable for the inputs an admin
* most needs warned about. The 64 is a denial-of-service limit, not a vocabulary.
*
* Owner: `previewQuerySchema` in `admin-user-app-visibility.controller.ts`.
*/
const getUserRoleAppVisibilityPreviewRequestSchema = z.strictObject({ role: z.string().min(1).max(64) });
/**
* The body of `GET /v1/admin/users/{id}/role-app-visibility-preview`.
*
* Counts, never names: the endpoint sizes a consequence and a body that also enumerated the org's
* catalog would be a wider surface than the job needs.
*
* `licensedAppCount` is the denominator both counts are drawn from — apps the org is licensed for
* that also resolve for this user's channel, never every app on the data plane. `delta` is
* `proposedVisibleAppCount - currentVisibleAppCount`, and negative is a loss. `losesAllApps` is
* true only when the user can see at least one app now and none afterwards; it is not
* `proposedVisibleAppCount === 0`, which also fires for a user who already sees nothing.
*
* Owner: `RoleChangeAppVisibilityPreview` in `admin-user-app-visibility.controller.ts`.
*/
const getUserRoleAppVisibilityPreviewResponseSchema = z.object({
	userId: z.uuid(),
	currentRole: z.string(),
	proposedRole: z.string(),
	licensedAppCount: z.number().int().min(0),
	currentVisibleAppCount: z.number().int().min(0),
	proposedVisibleAppCount: z.number().int().min(0),
	delta: z.number().int(),
	losesAllApps: z.boolean()
});
/**
* The body of `PATCH /v1/admin/users/{id}/role`.
*
* Shape only. The value is validated referentially inside the org closure by
* `assertRoleSlugResolves`, because a schema cannot reach `auth.org_role_definitions` and an enum
* cannot admit a custom role slug. No charset pattern: custom slugs are created through
* `POST /v1/admin/roles` under `z.string().min(1).max(256)`, so anything narrower would refuse a
* slug this system issued.
*
* Owner: `updateUserRoleSchema` in `admin-users.controller.ts`.
*/
const updateUserRoleRequestSchema = z.strictObject({ role: z.string().min(1).max(256) });
/**
* The body of `PATCH /v1/admin/users/{id}/role` — status 200, one key. The controller sends
* `{ message: 'Role updated' }`. The shell client types this call `Promise<void>`.
*/
const updateUserRoleResponseSchema = z.object({ message: z.string() });
/**
* The body of `DELETE /v1/admin/users/{id}` — status 200, `{ message: 'User deactivated' }`.
* Not a 204, and the shell client types it `Promise<void>`.
*/
const deactivateUserResponseSchema = z.object({ message: z.string() });
/**
* The body of `POST /v1/admin/users/{id}/reactivate` — status 200,
* `{ message: 'User reactivated' }`. The shell client types it `Promise<void>`.
*/
const reactivateUserResponseSchema = z.object({ message: z.string() });
/**
* The body of `PATCH /v1/admin/users/{id}`. Every key is optional and `UserService.updateUser`
* writes only the keys present.
*
* `displayName` is a bare string (zion-admin-app `uncovered-admin-routes` ruling R-010): the
* producer accepts `''` and any length on this route, so a form that re-sends a loaded name is
* never refused here. The admin app's form owns the 1..256 check and sends only the keys that
* changed. The accept-invitation writer, a different route, bounds its own name 1..256 (#3331).
* `avatarUrl` is a string of at most 2048 characters, or `null`, and `null` clears it. The
* contract fixes no URL grammar because the producer declares none; a client that renders the
* value into an `img src` applies its own scheme check before send (zion-admin-app
* `uncovered-admin-routes` ruling R-002). The 2048 is a guess with no measurement behind it, and
* `users.avatar_url` is a `varchar` with no length; the producer binds this schema (#3331), so
* the bound holds on both sides.
*
* `status` is `active` or `deactivated`, the producer's `ADMIN_WRITABLE_USER_STATUSES`.
* `removed` is refused here, because {@link REMOVE_MEMBER_ROUTE} is that value's only writer
* (ruling R11). No `email` and no `role`: role changes go through {@link UPDATE_USER_ROLE_ROUTE}.
*
* Owner: `updateUserSchema` in `admin-users.controller.ts`, an alias of this schema since #3331.
*/
const updateUserRequestSchema = z.strictObject({
	displayName: z.string().optional(),
	avatarUrl: z.string().max(2048).nullable().optional(),
	status: z.enum(["active", "deactivated"]).optional()
});
/**
* The body of `PATCH /v1/admin/users/{id}` — the saved row through `toAdminUserView`, the same
* 16 fields the list row carries without `extraRoleCount`.
*/
const updateUserResponseSchema = adminUserViewSchema;
/**
* The body of `DELETE /v1/admin/users/{id}/membership` — status 200,
* `{ message: 'Member removed' }`.
*/
const removeMemberResponseSchema = z.object({ message: z.string() });
/**
* The body of `POST /v1/admin/users/{id}/transfer-ownership` —
* `{ message: 'Ownership transferred' }`.
*/
const transferOwnershipResponseSchema = z.object({ message: z.string() });
/**
* The body of `DELETE /v1/admin/users/{id}/sessions` — status 200,
* `{ message: 'Sessions revoked' }`.
*/
const revokeUserSessionsResponseSchema = z.object({ message: z.string() });
/** Every route this file names, for `route-census.ts`. */
const usersRoutes = Object.freeze([
	LIST_USERS_ROUTE,
	GET_USER_ROUTE,
	GET_USER_ROLE_APP_VISIBILITY_PREVIEW_ROUTE,
	UPDATE_USER_ROLE_ROUTE,
	DEACTIVATE_USER_ROUTE,
	REACTIVATE_USER_ROUTE,
	UPDATE_USER_ROUTE,
	REMOVE_MEMBER_ROUTE,
	TRANSFER_OWNERSHIP_ROUTE,
	REVOKE_USER_SESSIONS_ROUTE
]);
//#endregion
export { DEACTIVATE_USER_ROUTE, GET_USER_ROLE_APP_VISIBILITY_PREVIEW_ROUTE, GET_USER_ROUTE, LIST_USERS_ROUTE, REACTIVATE_USER_ROUTE, REMOVE_MEMBER_ROUTE, REVOKE_USER_SESSIONS_ROUTE, TRANSFER_OWNERSHIP_ROUTE, UPDATE_USER_ROLE_ROUTE, UPDATE_USER_ROUTE, adminUserMembershipPath, adminUserPasskeySchema, adminUserPath, adminUserReactivatePath, adminUserRoleAppVisibilityPreviewPath, adminUserRolePath, adminUserSessionSchema, adminUserSessionsPath, adminUserTransferOwnershipPath, adminUserViewSchema, deactivateUserResponseSchema, effectiveRoleSchema, getUserResponseSchema, getUserRoleAppVisibilityPreviewRequestSchema, getUserRoleAppVisibilityPreviewResponseSchema, listUsersRequestSchema, listUsersResponseSchema, reactivateUserResponseSchema, removeMemberResponseSchema, revokeUserSessionsResponseSchema, transferOwnershipResponseSchema, updateUserRequestSchema, updateUserResponseSchema, updateUserRoleRequestSchema, updateUserRoleResponseSchema, userChannelGrantSchema, userDetailSchema, userGroupRefSchema, userListItemSchema, usersRoutes };

//# sourceMappingURL=users.js.map